import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'

import { REQUEST_SEARCH_QUERY_LENGTH } from '../../../util/consts'
import { theme } from '../../../themes/theme'
import { FlexRow } from '../../styles/Containers'
import { SUBTLE_FILL } from '../../styles/colors'
import { TextField } from '../../styles/TextField'
import Button, { SecondaryIconButton } from '../../common/Button'

export const SEARCH_HELPER_TEXT = 'Press Enter or Search. Searches file names and text of converted and combined files across all categories.'
export const SEARCH_TOGGLE_LABEL = 'Search this request'
export const SEARCH_COLLAPSE_LABEL = 'Hide search'

const SEARCH_ROW_HEIGHT_PX = 38

const Group = styled(FlexRow)`
  align-items: center;
  gap: 5px;
  min-width: 0;
`

const ToggleButton = styled(SecondaryIconButton)`
  height: ${SEARCH_ROW_HEIGHT_PX}px;
  width: ${SEARCH_ROW_HEIGHT_PX}px;

  .MuiSvgIcon-root {
    height: 20px;
    width: 20px;
  }

  &[data-expanded='true'] {
    background: ${theme.colors.primaryExtraLight};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`

const Form = styled.form`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  gap: 5px;
`

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: none;
  background: transparent;
  padding: 3px;
  border-radius: 50%;
  margin-right: 5px;
  cursor: pointer;
  color: ${theme.colors.inputField.placeholder};

  :hover {
    background: ${SUBTLE_FILL};
    color: ${theme.colors.text.body};
  }

  .MuiSvgIcon-root {
    height: 16px;
    width: 16px;
    color: inherit;
  }
`

// Expandable search field with explicit submit and persistent query state.
function RequestSearchBar({
  expanded,
  onToggle,
  onCollapse,
  value,
  onChange,
  onSubmit,
  onClear,
  searching = false,
  validationMessage = null,
  panelId
}) {
  const canSubmit = !searching
  const invalid = Boolean(validationMessage)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!expanded) return
    const input = inputRef.current
    if (!input) return
    input.focus()
    const end = input.value.length
    input.setSelectionRange?.(end, end)
  }, [expanded])

  function handleSubmit(event) {
    event.preventDefault()
    if (canSubmit) onSubmit(value)
  }

  function handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      onCollapse()
    }
  }

  return (
    <Group>
      <ToggleButton
        type="button"
        onClick={onToggle}
        aria-label={expanded ? SEARCH_COLLAPSE_LABEL : SEARCH_TOGGLE_LABEL}
        title={expanded ? SEARCH_COLLAPSE_LABEL : SEARCH_TOGGLE_LABEL}
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
        data-expanded={expanded ? 'true' : 'false'}
        data-testid="request-search-toggle">
        <SearchIcon />
      </ToggleButton>
      {expanded && (
        <Form role="search" aria-label={SEARCH_TOGGLE_LABEL} onSubmit={handleSubmit}>
          <TextField
            inputRef={inputRef}
            variant="standard"
            type="text"
            value={value}
            onChange={event => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search records..."
            error={invalid}
            haslabel="false"
            InputProps={{ disableUnderline: true }}
            slotProps={{
              htmlInput: {
                'aria-label': 'Search file names and contents in this request',
                'aria-invalid': invalid,
                autoComplete: 'off',
                enterKeyHint: 'search',
                maxLength: REQUEST_SEARCH_QUERY_LENGTH.max + 50
              },
              input: {
                endAdornment: (
                  <>
                    {value && (
                      <ClearButton type="button" onClick={onClear} aria-label="Clear search" title="Clear search">
                        <CloseIcon />
                      </ClearButton>
                    )}
                  </>
                )
              }
            }}
          />
          <Button type="submit" disabled={!canSubmit} aria-label="Search" loading={searching}>
            Search
          </Button>
        </Form>
      )}
    </Group>
  )
}

RequestSearchBar.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onCollapse: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  searching: PropTypes.bool,
  validationMessage: PropTypes.string,
  panelId: PropTypes.string.isRequired
}

export default RequestSearchBar
